import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Build responsive SVG layouts with react-svg-flexbox",
  "description": "",
  "published": "2018-08-17T00:00:00.000Z",
  "redirect_from": ["/blog/build-easier-responsive-svg-layout-react-svg-flexbox/swizec/8577"],
  "categories": "Front End, react, svg, Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`SVG is great. Best way to build scalable graphics on the web. SVG can do everything from simple logos to data visualization and even animation.`}</p>
    <p>{`The best part is, you can manipulate SVG with both CSS `}<em parentName="p">{`and`}</em>{` JavaScript. It's an image that's part of your DOM. 🤯`}</p>
    <p>{`Look at this animated fire example. Isn't it neat? No animated image required, just some SVG elements and a bit of JavaScript.`}</p>
    <p><a parentName="p" {...{
        "href": "//codepen.io/ste-vg/embed/oZXmdL/?height=265&theme-id=0&default-tab=html,result&embed-version=2"
      }}>{`SVG Fire 🔥`}</a></p>
    <p>{`No wonder then that SVG is the norm when it comes to data visualization and other programmable graphics.`}</p>
    <p>{`Just one problem: `}<strong parentName="p">{`SVG sucks at layout`}</strong></p>
    <p>{`Absolutely terrible. There's no layout support at all. You get absolute positioning and that's it. Sure, you can position absolutely within absolutely positioned elements, which is sort of relative positioning, but ugh ...`}</p>
    <h2 {...{
      "id": "absolute-positioning-hell"
    }}>{`Absolute positioning hell`}</h2>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/08/react-d3-scatterplot-dashboard.png",
        "alt": "A React + D3 dashboard of scatterplots"
      }}></img></p>
    <p>{`Say you're building a small dashboard. Like of different scatterplots looking at a dataset about dog breeds.`}</p>
    <p>{`Because the data is there, and you can, of course.`}</p>
    <p>{`You create a scatterplot component. It takes an `}<inlineCode parentName="p">{`x`}</inlineCode>{` and a `}<inlineCode parentName="p">{`y`}</inlineCode>{` position and sizing info. Inside, it places two axes, a caption, and the datapoints.`}</p>
    <p>{`The `}<inlineCode parentName="p">{`<Scatterplot>`}</inlineCode>{` is absolutely positioned via a `}<inlineCode parentName="p">{`translate`}</inlineCode>{` transformation. That moves an SVG element by a vector from `}<inlineCode parentName="p">{`(0, 0)`}</inlineCode>{`, thus rendering at the `}<inlineCode parentName="p">{`(x, y)`}</inlineCode>{` coordinate.`}</p>
    <p>{`You render each scatterplot like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`<scatterplot data={data} x={100} y={100} width={350} height={350} filter="{d" =="">
            d.weight &&
            d.height &&
            d.weight[0] &&
            d.height[0]
        }
        xData={d => d.weight[0]}
        yData={d => d.height[0]}
        xLabel="Weight (lbs)"
        yLabel="Height (in)"
        title="Dog Breed Height & Weight"
        entry={props => (
            <datapoint breed={props.d.breed} {...props}="">
        )}
    />
</datapoint></scatterplot>
`}</code></pre>
    <p>{`A lot of arguments, I know. Data, positioning, sizing, functions for interpreting data, a few labels, and a render prop for each datapoint.`}</p>
    <p>{`Once positioned with a grouping element `}<inlineCode parentName="p">{`<g>`}</inlineCode>{`, the scatterplot can use quasi-relative positioning for its elements.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`render() {
    return (
        <g transform="{\`translate(\${x}," \${y})\`}="">
            <heading y={-25}>{title}</heading>
            {data.map(d =>
                entry({
                    x: xScale(xData(d)),
                    y: yScale(yData(d)),
                    d: d
                })
            )}
            <axis scale={xScale} x={0} y={height} type="Bottom" label={xLabel}>
            <axis scale={yScale} x={0} y={0} type="Left" label={yLabel}>
        </axis></axis></g>
    );
}
`}</code></pre>
    <p>{`Position the main container at `}<inlineCode parentName="p">{`x`}</inlineCode>{` and `}<inlineCode parentName="p">{`y`}</inlineCode>{`. Place `}<inlineCode parentName="p">{`Heading`}</inlineCode>{` at `}<inlineCode parentName="p">{`(0, -25)`}</inlineCode>{`, datapoints at wherever they go, one axis at `}<inlineCode parentName="p">{`(0, 0)`}</inlineCode>{`, and another at left bottom. All relative to the parent container.`}</p>
    <p>{`The parent container is technically relative to the whole SVG container ... 🤔`}</p>
    <p>{`Absolute or relative, doesn't matter. You're gonna have one hell of a fun time calculating the position of each individual element by hand. D3 scales help, but you still have to think about it.`}</p>
    <p>{`SVG itself offers zero help.`}</p>
    <p>{`Want to resize your scatterplots? Here's what happens 👇`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/08/scatterplots-spaced-out.png",
        "alt": "Smaller scatterplots, gotta update positions"
      }}></img></p>
    <p>{`Want to resize your browser? Here's what happens 👇`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/08/not-responsive-at-all.png",
        "alt": "Resized browser cuts off visualization"
      }}></img></p>
    <p>{`Ugh.`}</p>
    <h2 {...{
      "id": "react-svg-flexbox-to-the-rescue"
    }}>{`react-svg-flexbox to the rescue`}</h2>
    <p>{`You can fix the layouting problem with `}<a parentName="p" {...{
        "href": "https://github.com/zoopoetics/react-svg-flexbox"
      }}><inlineCode parentName="a">{`react-svg-flexbox`}</inlineCode></a>{`. It's a small library, not a lot of stars, but omg so perfect.`}</p>
    <p>{`Built on top of Facebook's `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/package/css-layout"
      }}>{`css-layout`}</a>{`, which has recently become a part of `}<a parentName="p" {...{
        "href": "https://github.com/facebook/yoga"
      }}>{`yoga`}</a>{`, it lets you use CSS flexbox to position SVG elements.`}</p>
    <p>{`Flexbox might be confusing to grok – I look at tutorials any time I use it for anything – but it's way better than doing it yourself. How many engineers worked on browser layout engines over the past two decades?`}</p>
    <p>{`Wouldn't wanna retrace all those steps yourself 😅`}</p>
    <p>{`Wrap our dashboard in a `}<inlineCode parentName="p">{`<Flexbox>`}</inlineCode>{` element and…`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`import Flexbox from "react-svg-flexbox";

// ...

// render() etc.
<svg>
    <flexbox style={{ flexdirection: "row", justifycontent: "flex-start" }}>
        <scatterplot data={data} width={200} height={200} filter="{d" =="">
                d.weight &&
                d.height &&
                d.weight[0] &&
                d.height[0]
            }
            xData={d => d.weight[0]}
            yData={d => d.height[0]}
            xLabel="Weight (lbs)"
            yLabel="Height (in)"
            title="Dog Breed Height & Weight"
            entry={props => (
                <datapoint breed={props.d.breed} {...props}=""></datapoint>
            )}
        />
</scatterplot></flexbox></svg>
`}</code></pre>
    <p>{`We take `}<inlineCode parentName="p">{`<Flexbox>`}</inlineCode>{` out of `}<inlineCode parentName="p">{`react-svg-flexbox`}</inlineCode>{`, use flexbox styles to say we want to render in a row that starts at the beginning, and the rest happens on its own.`}</p>
    <p>{`Note that react-svg-flexbox passes `}<inlineCode parentName="p">{`x`}</inlineCode>{` and `}<inlineCode parentName="p">{`y`}</inlineCode>{` props into our components, so we had to take out manual positioning. Our dashboard now uses up all the space it can 👇`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/08/scatterplots-in-a-row.png",
        "alt": "Scatterplots rendered in a flexbox row"
      }}></img></p>
    <p>{`Something's funky with our vertical positioning, but it's an easy fix. Offset `}<inlineCode parentName="p">{`y`}</inlineCode>{` coordinates by a few pixels. ¯`}{`_`}{`(ツ)`}{`_`}{`/¯ it's not happening in the official examples, so it must be something we're doing inside those scatterplots.`}</p>
    <p>{`The axes are still poking outside our `}<inlineCode parentName="p">{`width`}</inlineCode>{` bounds, but this looks better already.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/08/scatterplots-vertical-offset.png",
        "alt": "Scatterplots with a vertical offset"
      }}></img></p>
    <p>{`Even if we make one of them bigger, everything still works. No changes to positioning required! o/`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/08/one-big-scatterplot.png",
        "alt": "One big scatterplot"
      }}></img></p>
    <h2 {...{
      "id": "responsive-layout-with-react-svg-flexbox"
    }}>{`Responsive layout with react-svg-flexbox`}</h2>
    <p>{`For the biggest win, we add `}<inlineCode parentName="p">{`flexWrap: wrap`}</inlineCode>{` to our `}<inlineCode parentName="p">{`<Flexbox>`}</inlineCode>{` component. Like this 👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{` <flexbox 1024="" style={{ flexdirection: "row", justifycontent: "flex-start", flexwrap: "wrap", width: 1024, height: }}>
</flexbox>
`}</code></pre>
    <p>{`You have to specify available width in your styles, otherwise, it doesn't work. That means you should listen to `}<inlineCode parentName="p">{`window.onresize`}</inlineCode>{` and update width accordingly.`}</p>
    <p>{`Easiest to attach an event listener in `}<inlineCode parentName="p">{`componentDidMount`}</inlineCode>{`. Like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`class App extends Component {
    state = {
        width: 1024
    };

    svgRef = React.createRef();

    componentDidMount() {
        window.addEventListener("resize", this.updateSize);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.updateSize);
    }

    updateSize = () => {
        this.setState({ width: this.svgRef.current.clientWidth });
    };

    render() {
        // ...

        <svg 1024="" style={{ width: "100%", height: }} ref={this.svgRef}>
            <flexbox 1024="" style={{ flexdirection: "row", justifycontent: "flex-start", flexwrap: "wrap", width: this.state.width, height: }}>
    }
</flexbox></svg>
`}</code></pre>
    <p>{`And your dashboard becomes responsive! Yay`}</p>
    <blockquote {...{
      "className": "twitter-tweet",
      "data-dnt": "true"
    }}><p parentName="blockquote" {...{
        "lang": "en",
        "dir": "ltr"
      }}>{`Hey look, a responsive dataviz dashboard made with React. It's all one big SVG 🤘`}<br parentName="p"></br><br parentName="p"></br>{`Article out tomorrow `}<a parentName="p" {...{
          "href": "https://t.co/4hgMOYXqaL"
        }}>{`pic.twitter.com/4hgMOYXqaL`}</a></p>{`— Swizec Teller published ServerlessHandbook.dev (@Swizec) `}<a parentName="blockquote" {...{
        "href": "https://twitter.com/Swizec/status/1030372365006327808"
      }}>{`August 17, 2018`}</a></blockquote>
    <h2 {...{
      "id": "see-the-code-play-with-examples"
    }}>{`See the code, play with examples`}</h2>
    <p>{`You can see a full set of `}<a parentName="p" {...{
        "href": "https://zoopoetics.github.io/react-svg-flexbox/"
      }}>{`react-svg-flexbox examples on their Storybook`}</a>{`.`}</p>
    <p>{`Code for my dog breed dashboard example is on GitHub `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/dog-size-intelligence-popularity"
      }}>{`here`}</a>{`.`}</p>
    <p>{`You can `}<a parentName="p" {...{
        "href": "https://build-qoyobtahea.now.sh"
      }}>{`try it live here`}</a>{`.`}</p>
    <h2 {...{
      "id": "fin"
    }}>{`Fin`}</h2>
    <p>{`Use react-svg-flexbox. Your life will improve. The best thing that's ever happened to me for SVG coding.`}</p>
    <p>{`Thanks `}<a parentName="p" {...{
        "href": "https://twitter.com/code_e_averett"
      }}>{`Cody Averett`}</a>{` for finding this gem 💎`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      